<template>
  <div class="devotional-card">
    <div v-if="loading" class="devotional-loading">
      <div class="spinner"></div>
      <p>Loading today's devotional...</p>
    </div>
    
    <div v-else-if="error" class="devotional-error">
      <div class="error-icon">📖</div>
      <p>{{ error }}</p>
    </div>
    
    <div v-else-if="devotional" class="devotional-content">
      <!-- Show metadata header if available (KV format) -->
      <div v-if="metadata" class="devotional-header">
        <span class="devotional-date">{{ formattedDate }}</span>
        <span class="devotional-day" v-if="metadata.day">Day {{ metadata.day }}</span>
      </div>
      
      <!-- Show old JSON format header if no metadata -->
      <div v-else-if="devotional.day" class="devotional-header">
        <span class="devotional-date">{{ formattedDate }}</span>
        <span class="devotional-day">Day {{ devotional.day }}</span>
      </div>
      
      <!-- Audio Player (if available) -->
      <div v-if="metadata && metadata.audioUrl" class="devotional-audio-player">
        <div class="audio-header">
          <span class="audio-icon">🎧</span>
          <h4>Listen to Today's Devotional</h4>
        </div>
        <audio controls :src="metadata.audioUrl" class="audio-player">
          Your browser does not support the audio element.
        </audio>
      </div>
      
      <!-- Render HTML content from KV (sanitized server-side) -->
      <div v-if="htmlContent" class="devotional-html" v-html="htmlContent"></div>
      
      <!-- Fallback: Render JSON format (for development/backward compatibility) -->
      <template v-else-if="devotional.title">
        <h3 class="devotional-title">{{ devotional.title }}</h3>
        
        <div class="devotional-scripture">
          <div class="scripture-icon">📖</div>
          <div class="scripture-content">
            <p class="scripture-reference">{{ devotional.scripture.reference }}</p>
            <div class="scripture-verses">
              <p v-for="(verse, index) in devotional.scripture.verses" :key="index" class="scripture-text">
                {{ verse.text }}
              </p>
            </div>
          </div>
        </div>
        
        <div class="devotional-thought">
          <p>{{ truncatedThought }}</p>
          <button v-if="!expanded && needsTruncation" @click="expanded = true" class="read-more-btn">
            Read More →
          </button>
          <button v-if="expanded" @click="expanded = false" class="read-more-btn">
            Show Less ←
          </button>
        </div>
        
        <div class="devotional-prayer">
          <h4>Prayer</h4>
          <p class="prayer-text">{{ devotional.prayer }}</p>
        </div>
        
        <div class="devotional-invitation">
          <p>{{ devotional.invitation }}</p>
        </div>
      </template>
      
      <div class="devotional-actions">
        <button @click="shareDevotional" class="action-btn share-btn">
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M4 12v8a2 2 0 002 2h12a2 2 0 002-2v-8M16 6l-4-4-4 4M12 2v13" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Share
        </button>
        <button @click="printDevotional" class="action-btn print-btn">
          <svg class="btn-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor">
            <path d="M6 9V2h12v7M6 18H4a2 2 0 01-2-2v-5a2 2 0 012-2h16a2 2 0 012 2v5a2 2 0 01-2 2h-2M6 14h12v8H6v-8z" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
          </svg>
          Print
        </button>
      </div>
    </div>
  </div>
</template>

<script>
export default {
  name: 'DevotionalCard',
  emits: ['devotional-loaded', 'devotional-error'],
  data() {
    return {
      devotional: null,
      metadata: null,
      htmlContent: null,
      loading: true,
      error: null,
      expanded: false
    }
  },
  computed: {
    formattedDate() {
      const dateStr = this.metadata?.date?.length === 10 ? this.metadata.date : (this.devotional?.date?.length === 10 ? this.devotional.date : null);
      if (!dateStr && this.metadata?.date?.length === 5) return new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
      if (!dateStr) return '';
      // Parse as local date to avoid timezone shifting (e.g., AKST showing previous day)
      const parts = dateStr.split('-');
      if (parts.length !== 3) return '';
      const year = parseInt(parts[0], 10);
      const month = parseInt(parts[1], 10) - 1; // JS months are 0-based
      const day = parseInt(parts[2], 10);
      const date = new Date(year, month, day);
      return date.toLocaleDateString('en-US', { 
        weekday: 'long', 
        year: 'numeric', 
        month: 'long', 
        day: 'numeric' 
      });
    },
    needsTruncation() {
      return this.devotional?.devotionalThought && this.devotional.devotionalThought.length > 400;
    },
    truncatedThought() {
      if (!this.devotional?.devotionalThought) return '';
      if (this.expanded || !this.needsTruncation) {
        return this.devotional.devotionalThought;
      }
      return this.devotional.devotionalThought.substring(0, 400) + '...';
    }
  },
  mounted() {
    this.loadDevotional();
  },
  methods: {
    async loadDevotional() {
      try {
        this.loading = true;
        this.error = null;
        
        // Get today's date
        const today = new Date();
        const year = today.getFullYear();
        const month = String(today.getMonth() + 1).padStart(2, '0');
        const day = String(today.getDate()).padStart(2, '0');
        const dateStr = `${year}-${month}-${day}`;
        
        // Try API endpoint first (production with KV)
        try {
          const apiResponse = await fetch(`/api/devotional/${dateStr}`);
          if (apiResponse.ok) {
            const result = await apiResponse.json();
            
            if (result.success && result.data) {
              // Check if it's the new KV format (metadata + html)
              if (result.data.metadata && result.data.html) {
                // Additional client-side check for published status
                if (result.data.metadata.published === false) {
                  throw new Error('Devotional not yet published');
                }
                
                this.metadata = result.data.metadata;
                this.htmlContent = result.data.html;
                // Keep a minimal devotional object for sharing
                this.devotional = {
                  title: this.metadata.title,
                  date: this.metadata.date,
                  scripture: { reference: this.metadata.scriptureRef }
                };
              } 
              // Old JSON format or fallback with full devotional object
              else if (result.data.devotional) {
                // Check published status for JSON format
                if (result.data.devotional.published === false) {
                  throw new Error('Devotional not yet published');
                }
                this.devotional = result.data.devotional;
              }
              // Direct devotional data
              else {
                // Check published status
                if (result.data.published === false) {
                  throw new Error('Devotional not yet published');
                }
                this.devotional = result.data;
              }
              this.$emit('devotional-loaded');
              return;
            }
          }
        } catch (apiError) {
          console.log('API fetch failed, trying local file:', apiError.message);
        }
        
        // Fallback: Try to load from local file (for development)
        const filePath = `/src/content/devotionals/${year}/${month}/day${day}.json`;
        const response = await fetch(filePath);
        
        if (response.ok) {
          this.devotional = await response.json();
          this.$emit('devotional-loaded');
        } else {
          throw new Error('No devotional available for today');
        }
        
      } catch (err) {
        console.error('Error loading devotional:', err);
        this.error = 'Unable to load today\'s devotional. Please try again later.';
        this.$emit('devotional-error');
      } finally {
        this.loading = false;
      }
    },
    shareDevotional() {
      const title = this.metadata?.title || this.devotional?.title || 'Devotional';
      const scriptureRef = this.metadata?.scriptureRef || this.devotional?.scripture?.reference || '';
      const excerpt = this.metadata?.excerpt || this.devotional?.devotionalThought?.substring(0, 200) || '';
      
      if (navigator.share) {
        navigator.share({
          title: `Devotional: ${title}`,
          text: `${scriptureRef}\n\n${excerpt}...`,
          url: window.location.href
        }).catch(err => {
          console.log('Error sharing:', err);
        });
      } else {
        // Fallback: copy to clipboard
        const text = `${title}\n${scriptureRef}\n\n${window.location.href}`;
        navigator.clipboard.writeText(text).then(() => {
          alert('Devotional link copied to clipboard!');
        });
      }
    },
    printDevotional() {
      window.print();
    }
  }
}
</script>

<style scoped>
.devotional-card {
  background: var(--white);
  border-radius: 20px;
  padding: 40px;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  border: 2px solid var(--gray-200);
  max-width: 800px;
  margin: 0 auto;
}

.devotional-loading,
.devotional-error {
  text-align: center;
  padding: 60px 20px;
}

.spinner {
  width: 50px;
  height: 50px;
  margin: 0 auto 20px;
  border: 4px solid var(--gray-200);
  border-top-color: var(--primary-color);
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  to { transform: rotate(360deg); }
}

.devotional-loading p,
.devotional-error p {
  color: var(--gray-600);
  font-size: 1.0625rem;
}

.error-icon {
  font-size: 3rem;
  margin-bottom: 15px;
}

/* Styles for HTML content from KV */
.devotional-html h1 {
  font-size: 2rem;
  font-weight: 800;
  color: var(--gray-900);
  margin-bottom: 25px;
  line-height: 1.3;
}

.devotional-html h2 {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 25px 0 12px;
}

.devotional-html time {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.devotional-html .scripture {
  background: linear-gradient(135deg, rgba(30, 136, 229, 0.05), rgba(0, 172, 193, 0.05));
  padding: 25px;
  border-radius: 15px;
  border-left: 4px solid var(--primary-color);
  margin-bottom: 30px;
}

.devotional-html .scripture .reference {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.devotional-html blockquote {
  margin: 0;
  padding: 0;
  border: none;
}

.devotional-html blockquote p {
  font-size: 1.0625rem;
  line-height: 1.8;
  color: var(--gray-800);
  font-style: italic;
  margin: 10px 0;
}

.devotional-html blockquote sup {
  font-weight: 600;
  color: var(--primary-color);
  margin-right: 4px;
}

.devotional-html .devotional-thought p {
  font-size: 1.0625rem;
  line-height: 1.9;
  color: var(--gray-700);
  margin-bottom: 15px;
}

.devotional-html .prayer {
  background: var(--gray-50);
  padding: 25px;
  border-radius: 15px;
  margin-bottom: 25px;
}

.devotional-html .prayer h2::before {
  content: '🙏 ';
  margin-right: 8px;
}

.devotional-html .prayer-text {
  font-size: 1rem;
  line-height: 1.8;
  color: var(--gray-700);
  font-style: italic;
}

.devotional-html .invitation {
  background: linear-gradient(135deg, rgba(30, 136, 229, 0.08), rgba(0, 172, 193, 0.08));
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  border: 1px solid var(--primary-light);
}

.devotional-html .invitation p {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--gray-700);
  margin: 0;
  text-align: center;
}

.devotional-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 2px solid var(--gray-100);
}

.devotional-date {
  font-size: 0.875rem;
  font-weight: 600;
  color: var(--gray-600);
  text-transform: uppercase;
  letter-spacing: 0.5px;
}

.devotional-day {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--white);
  background: linear-gradient(135deg, var(--primary-color), var(--accent-color));
  padding: 6px 16px;
  border-radius: 20px;
}

/* Audio Player Styles */
.devotional-audio-player {
  background: linear-gradient(135deg, rgba(30, 136, 229, 0.05), rgba(0, 172, 193, 0.05));
  padding: 20px;
  border-radius: 15px;
  margin-bottom: 30px;
  border: 2px solid var(--primary-light);
}

.audio-header {
  display: flex;
  align-items: center;
  gap: 10px;
  margin-bottom: 15px;
}

.audio-icon {
  font-size: 1.5rem;
}

.audio-header h4 {
  font-size: 1rem;
  font-weight: 700;
  color: var(--gray-900);
  margin: 0;
}

.audio-player {
  width: 100%;
  height: 40px;
  border-radius: 8px;
  outline: none;
}

.audio-player::-webkit-media-controls-panel {
  background: var(--white);
  border-radius: 8px;
}

.devotional-title {
  font-size: 2rem;
  font-weight: 800;
  color: var(--gray-900);
  margin-bottom: 25px;
  line-height: 1.3;
}

.devotional-scripture {
  display: flex;
  gap: 15px;
  background: linear-gradient(135deg, rgba(30, 136, 229, 0.05), rgba(0, 172, 193, 0.05));
  padding: 25px;
  border-radius: 15px;
  border-left: 4px solid var(--primary-color);
  margin-bottom: 30px;
}

.scripture-icon {
  font-size: 1.75rem;
  flex-shrink: 0;
}

.scripture-content {
  flex: 1;
}

.scripture-reference {
  font-size: 0.875rem;
  font-weight: 700;
  color: var(--primary-color);
  text-transform: uppercase;
  letter-spacing: 0.5px;
  margin-bottom: 10px;
}

.scripture-verses {
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.scripture-text {
  font-size: 1.0625rem;
  line-height: 1.8;
  color: var(--gray-800);
  font-style: italic;
  margin: 0;
}

.devotional-thought {
  margin-bottom: 30px;
}

.devotional-thought p {
  font-size: 1.0625rem;
  line-height: 1.9;
  color: var(--gray-700);
  margin-bottom: 15px;
}

.read-more-btn {
  background: none;
  border: none;
  color: var(--primary-color);
  font-weight: 600;
  font-size: 0.9375rem;
  cursor: pointer;
  padding: 8px 0;
  transition: all var(--transition-normal);
}

.read-more-btn:hover {
  color: var(--primary-dark);
  transform: translateX(3px);
}

.devotional-prayer {
  background: var(--gray-50);
  padding: 25px;
  border-radius: 15px;
  margin-bottom: 25px;
}

.devotional-prayer h4 {
  font-size: 1.125rem;
  font-weight: 700;
  color: var(--gray-900);
  margin-bottom: 12px;
  display: flex;
  align-items: center;
  gap: 8px;
}

.devotional-prayer h4::before {
  content: '🙏';
  font-size: 1.25rem;
}

.prayer-text {
  font-size: 1rem;
  line-height: 1.8;
  color: var(--gray-700);
  font-style: italic;
  margin: 0;
}

.devotional-invitation {
  background: linear-gradient(135deg, rgba(30, 136, 229, 0.08), rgba(0, 172, 193, 0.08));
  padding: 20px;
  border-radius: 12px;
  margin-bottom: 25px;
  border: 1px solid var(--primary-light);
}

.devotional-invitation p {
  font-size: 0.9375rem;
  line-height: 1.7;
  color: var(--gray-700);
  margin: 0;
  text-align: center;
}

.devotional-actions {
  display: flex;
  gap: 15px;
  justify-content: center;
  padding-top: 20px;
  border-top: 2px solid var(--gray-100);
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 12px 24px;
  border: 2px solid var(--gray-300);
  background: var(--white);
  color: var(--gray-700);
  border-radius: 10px;
  font-size: 0.9375rem;
  font-weight: 600;
  cursor: pointer;
  transition: all var(--transition-normal);
}

.action-btn:hover {
  border-color: var(--primary-color);
  color: var(--primary-color);
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(30, 136, 229, 0.15);
}

.btn-icon {
  width: 18px;
  height: 18px;
  stroke-width: 2;
}

/* Print Styles */
@media print {
  .devotional-card {
    box-shadow: none;
    border: none;
    padding: 20px;
  }
  
  .devotional-actions {
    display: none;
  }
  
  .read-more-btn {
    display: none;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .devotional-card {
    padding: 25px 20px;
    border-radius: 15px;
  }
  
  .devotional-title {
    font-size: 1.5rem;
  }
  
  .devotional-scripture {
    padding: 20px;
  }
  
  .devotional-actions {
    flex-direction: column;
  }
  
  .action-btn {
    width: 100%;
    justify-content: center;
  }
}
</style>
